import React, { memo } from 'react'
import { Card } from 'transfix-ui/components/Card'
import { Box, Grid, makeStyles, Typography } from 'transfix-ui/core'
import { $enum } from 'ts-enum-util'
import { dataUnavailableForOlderRateMessage } from 'apps/marketplace/pages/spot-quote-tool/constants'
import {
  COST_CALC_TOOLTIP,
  FUEL_MILEAGE,
  ISpotRate,
  RPM_SOURCE,
} from 'apps/marketplace/store/amQuoting/types'
import { formatCurrency } from 'utils/formatCurrency'
import { CostCalTableRow } from './CostCalTableRow'

interface ICostCalculationCardProps {
  spotRate: Partial<ISpotRate> & { metadata: ISpotRate['metadata'] }
}

const useStyles = makeStyles(theme => ({
  cardContainer: {
    height: '100%',
  },
  bold: {
    fontWeight: theme.typography.fontWeightBold,
  },
}))

const NA_DNB_MSG = 'This data is unavailable when a Do Not Bid rule has been applied.'

export const CostCalculationCard = memo<ICostCalculationCardProps>(({ spotRate }) => {
  const classes = useStyles()

  const {
    metadata: { fuelSurchargeCalculation },
  } = spotRate

  const { calculatedFuelCost, fuelMileage, fuelMileageType, fuelRpm, fuelRpmType } =
    fuelSurchargeCalculation || {}

  const displayFuelRpm = fuelRpm ? `${fuelRpm} RPM` : NA_DNB_MSG

  const displayCalculatedFuelCost = calculatedFuelCost
    ? formatCurrency(calculatedFuelCost)
    : NA_DNB_MSG

  return (
    <Box>
      <div className='pb3'>
        <Typography variant='subtitle2'>Calculations</Typography>
      </div>
      <Grid container direction='row' spacing={2} className='pt3'>
        <Grid item xs={3} lg={6}>
          <Card className={classes.cardContainer}>
            <Typography variant='overline'>FUEL SURCHARGE</Typography>
            {fuelSurchargeCalculation ? (
              <table className='table'>
                <tbody>
                  <CostCalTableRow
                    label={$enum(FUEL_MILEAGE).getValueOrDefault(fuelMileageType) || ''}
                    value={`${fuelMileage} Miles`}
                    tooltip={$enum(COST_CALC_TOOLTIP).getValueOrDefault(fuelMileageType) || ''}
                  />
                  <CostCalTableRow
                    label={$enum(RPM_SOURCE).getValueOrDefault(fuelRpmType) || 'Fuel RPM'}
                    value={displayFuelRpm}
                    tooltip={$enum(COST_CALC_TOOLTIP).getValueOrDefault(fuelRpmType) || ''}
                  />
                  <CostCalTableRow
                    label='Estimated Fuel Surcharge'
                    value={displayCalculatedFuelCost}
                    tooltip={COST_CALC_TOOLTIP.fuel_surcharge}
                    labelClass={classes.bold}
                    valueClass={classes.bold}
                  />
                </tbody>
              </table>
            ) : (
              <Typography variant='h3' color='textSecondary'>
                {dataUnavailableForOlderRateMessage}
              </Typography>
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
})
